import { Component, ElementRef, HostListener, Injector, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { LoaderService } from '@app/main/cablepro/loader.service';
import { AppAuthService } from '@app/shared/common/auth/app-auth.service';
import { TrialBannerComponent } from '@app/shared/layout/trial-banner/trial-banner.component';
import { CommonModule } from '@node_modules/@angular/common';
import { NavigationEnd, Router } from '@node_modules/@angular/router';
import { NgbModal, NgbModalConfig } from '@node_modules/@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@node_modules/@ng-select/ng-select';
import { Subscription } from '@node_modules/rxjs';
import { filter } from '@node_modules/rxjs/operators';
import { AppConsts } from '@shared/AppConsts';
import { AppComponentBase, RequireLogin } from '@shared/common/app-component-base';
import { AppSessionService } from '@shared/common/session/app-session.service';
import { UserLoginInfoDto } from '@shared/service-proxies/service-proxies';
import { SharedService } from '@shared/service-proxies/shared.service';
import { FeatureCode } from '@shared/utils/feature-code';
import { LoginService } from 'account/login/login.service';
import { WebfacingLoginComponent } from 'webfacing/webfacing-login/webfacing-login.component';
import { WebfacingRegisterComponent } from 'webfacing/webfacing-register/webfacing-register.component';

/**
 * Interface for calculator navigation items
 */
export interface CalculatorNavItem {
  name: string;
  sortName: string;
  url?: string;
  external?: boolean;
  disabled?: boolean;
  icon?: string;
}

/**
 * Interface for standard variants (Cable Sizing variants)
 */
export interface StandardVariant {
  name: string;
  code: string;
  url: string;
  label: string;
}

/**
 * Interface for tab data
 */
export interface CalculatorTab {
  id: string;
  label: string;
  icon: string;
  url: string;
  isActive: boolean;
  svgPath?: string;
}

@Component({
  selector: 'app-webfacing-header-v2',
  templateUrl: './webfacing-header-v2.component.html',
  styleUrls: ['./webfacing-header-v2.component.scss'],
  standalone: true,
  imports: [CommonModule, NgSelectModule]
})
export class WebfacingHeaderV2Component extends AppComponentBase implements OnInit, OnDestroy {
  /**
   * Calculator page title (e.g., "Cable Sizing Calculator")
   * Gets automatically set based on current route if not provided
   */
  public calculatorName: string = '';

  /**
   * Standard variant for Cable Sizing (e.g., "AS/NZS 3008.1.1")
   * Gets automatically set based on current route if not provided
   */
  public standardVariant: string = 'AS/NZS 3008.1.1';

  /**
   * Last selected standard variant URL (persists when switching tabs)
   */
  public lastSelectedStandardVariantUrl: string = '/cable-sizing-as';

  /**
   * Whether to show the Generate Report button
   * Automatically determined based on current route
   */
  public showGenerateReport: boolean = false;

  /**
   * Whether it's a Cable Sizing page (shows standard dropdown)
   * Gets automatically set based on current route
   */
  public isCableSizingPage: boolean = false;

  // Internal state
  public user!: UserLoginInfoDto | undefined;
  public isAdminUser: boolean = false;
  public isAnnualProfessionalPlan: boolean = false;
  public isReportDisabled = true;
  private sub!: Subscription;
  public FeatureCode = FeatureCode;
  public isMobileMenuOpen: boolean = false;
  public isStandardsDropdownOpen: boolean = false;
  public isMobileCalcDropdownOpen: boolean = false;
  public isTabsMenuOpen: boolean = false;
  public isLocalhost: boolean = location.hostname === "localhost";

  // Standard variants for Cable Sizing
  public standardVariants: StandardVariant[] = [
    { name: 'AS/NZS 3008.1.1', code: 'as', url: '/cable-sizing-as', label: 'AS/NZS 3008.1.1 (Australian)' },
    { name: 'BS 7671', code: 'bs', url: '/cable-sizing-bs', label: 'BS 7671 (British)' },
    { name: 'IEC 60364-5-52', code: 'iec', url: '/cable-sizing-iec', label: 'IEC 60364-5-52 (International)' },
    { name: 'AS/NZS 3008 (NZ)', code: 'nz', url: '/cable-sizing-nz', label: 'AS/NZS 3008.1.2 (New Zealand)' },
    { name: 'NFPA 70 (NEC)', code: 'nec', url: '/cable-sizing-nec', label: 'NEC NFPA 70 (United States)' }
  ];

  // Calculator tabs for Row 2
  public calculatorTabs: CalculatorTab[] = [
    { id: 'cable-sizing', label: 'Cable Sizing', icon: 'flaticon-cable', svgPath: 'assets/custom/img/cable-sizing.svg', url: '/cable-sizing-as', isActive: false },
    { id: 'max-demand', label: 'Max Demand', icon: 'flaticon-chart', svgPath: 'assets/custom/img/max-demand.svg', url: '/max-demand', isActive: false },
    { id: 'cable-pulling', label: 'Cable Pulling', icon: 'flaticon-pull', svgPath: 'assets/custom/img/cable-pulling.svg', url: '/cable-pulling', isActive: false },
    { id: 'arc-flash', label: 'Arc Flash', icon: 'flaticon-warning', svgPath: 'assets/custom/img/arc-flash.svg', url: '/arc-flash', isActive: false },
    { id: 'protection-coordination', label: 'Protection Coordination', icon: 'flaticon-shield', svgPath: 'assets/custom/img/protection-coordination.svg', url: '/protection-coordination', isActive: false }
  ];

  // All available calculators for navigation
  public calculators: CalculatorNavItem[] = [
    { "name": "Cable Sizing Calculator", "sortName": "Cable Sizing AS/NZS 3008.1.1", url: 'cable-sizing-as' },
    { "name": "Cable Sizing Calculator", "sortName": "Cable Sizing BS 7671", url: 'cable-sizing-bs' },
    { "name": "Cable Sizing Calculator", "sortName": "Cable Sizing AS/NZS 3008.1.2", url: 'cable-sizing-nz' },
    { "name": "Cable Sizing Calculator", "sortName": "Cable Sizing IEC 60364-5-52", url: 'cable-sizing-iec' },
    { "name": "Cable Sizing Calculator", "sortName": "Cable Sizing NEC NFPA 70", url: 'cable-sizing-nec' },
    { "name": "Maximum Demand Calculator to AS/NZS 3000", "sortName": "Maximum Demand to AS/NZS 3000", url: 'max-demand' },
    { "name": "Protection Coordination Calculator", "sortName": "Protection Coordination", url: 'protection-coordination' },
    { "name": "Cable Pulling Tensions Calculator", "sortName": "Cable Pulling Tensions", url: 'cable-pulling' },
    { "name": "Arc Flash Calculator to IEEE 1584, ENA, DGUV, and DC methods", "sortName": "Arc Flash", url: 'arc-flash' },
    // { "name": "LV Network Calculator", url: 'https://cablepro.elek.com/create-free-account', external: true },
  ];

  public calculatorMappings = [
    { path: '/cable-sizing-as', id: 2 },
    { path: '/cable-sizing-nz', id: 3 },
    { path: '/cable-sizing-bs', id: 4 },
    { path: '/cable-sizing-iec', id: 5 },
    { path: '/cable-sizing-nec', id: 13 },
    { path: '/max-demand', id: 6 },
    { path: '/protection-coordination', id: 8 },
    { path: '/cable-pulling', id: 9 },
    { path: '/arc-flash', id: 10 }
  ];

  public _router: Router;
  redirectUrl: string = '/';
  showReportButton: boolean = false;

  // Storage keys for localStorage persistence
  private readonly STORAGE_KEY_STANDARD = 'cablepro_selected_standard';
  private readonly STORAGE_KEY_STANDARD_URL = 'cablepro_selected_standard_url';

  constructor(
    injector: Injector,
    private sharedService: SharedService,
    private _appSessionService: AppSessionService,
    public loginService: LoginService,
    private loaderService: LoaderService,
    private _authService: AppAuthService,
    private readonly router: Router,
    private modalService: NgbModal
  ) {
    super(injector);
    (window as any).wf_header_v2 = this;

    // Load saved values from localStorage
    this.loadFromLocalStorage();

    this.user = this._appSessionService.user;
    if (this.user) {
      this.isAdminUser = (this.user.id === 1 || this.user.id === 2);
    }
    console.log('WebfacingHeaderV2Component - User Info:', this.user);
    if (this.user?.premiumUser) {
      let IsUserHasProfessionalPlan = this.user?.userStripePlanLevel == 3;
      this.isAnnualProfessionalPlan = IsUserHasProfessionalPlan && this.user?.userStripePlanInterval?.toLowerCase().trim() === 'year';
    }
    this._router = router;

    // Initialize header properties based on current route
    this.initializeHeaderPropertiesFromRoute(router.url);
  }

  ngOnInit() {
    this.updateActiveTab(this.router.url);

    this.showReportButton = this.isReportButtonDisplay(this.router.url);

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.showReportButton = this.isReportButtonDisplay(event.url);
        this.updateActiveTab(event.url);
        this.initializeHeaderPropertiesFromRoute(event.url);
        this.closeAllMenus();
      });

    this.sub = this.loaderService.reportDisabled$
      .subscribe((disabled: boolean) => {
        this.isReportDisabled = disabled;
      });
  }

  ngOnDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  /**
   * Load standard variant and URL from localStorage
   */
  private loadFromLocalStorage(): void {
    try {
      const savedStandard = localStorage.getItem(this.STORAGE_KEY_STANDARD);
      const savedUrl = localStorage.getItem(this.STORAGE_KEY_STANDARD_URL);

      if (savedStandard) {
        this.standardVariant = savedStandard;
      }
      if (savedUrl) {
        this.lastSelectedStandardVariantUrl = savedUrl;
      }
    } catch (e) {
      console.warn('Failed to load from localStorage:', e);
    }
  }

  /**
   * Save standard variant and URL to localStorage
   */
  private saveToLocalStorage(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY_STANDARD, this.standardVariant);
      localStorage.setItem(this.STORAGE_KEY_STANDARD_URL, this.lastSelectedStandardVariantUrl);
    } catch (e) {
      console.warn('Failed to save to localStorage:', e);
    }
  }

  /**
   * Initialize calculator name, standard variant, and show report flag based on current route
   */
  private initializeHeaderPropertiesFromRoute(url: string): void {
    // Reset calculator name, keep standard variant default
    this.calculatorName = '';
    this.isCableSizingPage = false;
    this.showGenerateReport = false;

    this.setCalculatorNameFromRoute(url);
    // Determine if this is a cable sizing page and which standard
    this.isCableSizingPage = this.isCableSizingRoute(url);
    if (this.isCableSizingPage) {
      this.setCalculatorNameAndStandardFromRoute(url);
      // Show report dropdown only for cable sizing (not simple button)
      this.showGenerateReport = true;
    } else {
      // Set calculator name from other calculators
      // Reset to default standard variant when leaving cable sizing
      // this.standardVariant = 'AS/NZS 3008.1.1';
      // Show report button for non-cable-sizing calculators (not dropdown)
      this.showGenerateReport = !this.isHomeOrInvalidRoute(url);
    }
  }

  /**
   * Determine if current route is a cable sizing page
   */
  private isCableSizingRoute(url: string): boolean {
    return url.includes('/cable-sizing-as') ||
      url.includes('/cable-sizing-bs') ||
      url.includes('/cable-sizing-nz') ||
      url.includes('/cable-sizing-iec') ||
      url.includes('/cable-sizing-nec');
  }

  /**
   * Check if route is home or invalid (no calculator selected)
   */
  private isHomeOrInvalidRoute(url: string): boolean {
    return url === '/' || url === '' || !this.getCalculatorFromRoute(url);
  }

  /**
   * Get calculator object from route URL
   */
  private getCalculatorFromRoute(url: string): CalculatorNavItem | undefined {
    return this.calculators.find(calc =>
      calc.url && url.includes(calc.url)
    );
  }

  /**
   * Set calculator name and standard variant from cable sizing route
   */
  private setCalculatorNameAndStandardFromRoute(url: string): void {

    let currentStandard = this.standardVariants.find(variant => url.includes(variant.url));
    if (currentStandard) {
      this.standardVariant = currentStandard.label;
      this.lastSelectedStandardVariantUrl = currentStandard.url;
    }
    // Save to localStorage
    this.saveToLocalStorage();
  }

  /**
   * Set calculator name from other calculator routes
   */
  private setCalculatorNameFromRoute(url: string): void {
    const calculator = this.getCalculatorFromRoute(url);
    if (calculator) {
      this.calculatorName = calculator.name || '';
    }
    else if (url === '/' || url === '') {
      this.calculatorName = '';
    }
  }

  /**
   * Update active tab based on current route
   */
  private updateActiveTab(url: string): void {
    this.calculatorTabs.forEach(tab => {
      tab.isActive = url.includes(tab.id) || (tab.id === 'cable-sizing' && url.includes('cable-sizing'));
    });
  }

  /**
   * Navigate to calculator tab
   * For Cable Sizing, navigate to the last selected standard variant
   */
  onTabChange(tab: CalculatorTab): void {
    // Close all menus
    this.closeAllMenus();

    // If navigating to Cable Sizing, use the last selected standard variant URL
    let urlToNavigate = tab.url;
    if (tab.id === 'cable-sizing') {
      urlToNavigate = this.lastSelectedStandardVariantUrl;
    }
    if (location.hostname === "localhost") {
      this.router.navigate([urlToNavigate]);
    }
    else {
      location.href = `/calculators${urlToNavigate}`;
    }
  }

  /**
   * Toggle mobile menu
   */
  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    this.isMobileCalcDropdownOpen = false;
    this.isStandardsDropdownOpen = false;
    this.isTabsMenuOpen = false;
  }

  /**
   * Toggle tabs navigation menu (Bootstrap navbar)
   */
  toggleTabsMenu(): void {
    this.isTabsMenuOpen = !this.isTabsMenuOpen;
  }

  /**
   * Toggle mobile calculator dropdown
   */
  toggleMobileCalcDropdown(): void {
    this.isMobileCalcDropdownOpen = !this.isMobileCalcDropdownOpen;
    this.isMobileMenuOpen = false;
    this.isStandardsDropdownOpen = false;
    this.isTabsMenuOpen = false;
  }

  /**
   * Toggle standards dropdown
   */
  toggleStandardsDropdown(): void {
    console.log('Toggling standards dropdown. Current state:', this.isStandardsDropdownOpen);
    this.isStandardsDropdownOpen = !this.isStandardsDropdownOpen;
    console.log('Toggling standards dropdown. Current state:', this.isStandardsDropdownOpen);

  }

  /**
   * Close all menus
   */
  closeAllMenus(): void {
    this.isMobileMenuOpen = false;
    this.isMobileCalcDropdownOpen = false;
    this.isStandardsDropdownOpen = false;
    this.isTabsMenuOpen = false;
  }

  /**
   * Close mobile menu
   */
  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  /**
   * Close mobile calculator dropdown
   */
  closeMobileCalcDropdown(): void {
    this.isMobileCalcDropdownOpen = false;
  }

  /**
   * Get the label of the active calculator tab
   * Used for mobile navbar toggler display
   */
  getActiveTab() {
    let tab = this.calculatorTabs.find(tab => tab.isActive);
    return tab;
    // return activeTab ? activeTab.label : (this.calculatorTabs[0]?.label || 'Calculators');
  }

  /**
   * Get truncated mobile title
   * Removes "Calculator" word and truncates version numbers
   * Examples: "Cable Sizing Calculator AS/NZS 3008.1.1" → "Cable Sizing · AS/NZS"
   */
  getMobileTruncatedTitle(): string {
    let title = this.calculatorName.replace(' Calculator', '').trim();

    if (this.isCableSizingPage && this.standardVariant) {
      // Extract just the standard code (e.g., "AS/NZS" from "AS/NZS 3008.1.1")
      const standardCode = this.standardVariant.split(' ')[0];
      return `${title} · ${standardCode}`;
    }

    return title;
  }

  /**
   * Close standards dropdown when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const dropdown = document.querySelector('.item.cable-sizing');

    // Close dropdown if click is outside and dropdown is open
    if (dropdown && !dropdown.contains(target) && this.isStandardsDropdownOpen) {
      this.isStandardsDropdownOpen = false;
    }
  }

  /**
   * Handle escape key to close menus
   */
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (this.isMobileMenuOpen || this.isMobileCalcDropdownOpen) {
      event.preventDefault();
      this.closeAllMenus();
    }
  }

  /**
   * Handle standard variant selection (Cable Sizing only)
   * Saves the selected variant and navigates to it
   */
  onStandardChange(variant: StandardVariant): void {
    if (variant.url) {
      console.log('Standard change requested:', variant);
      // Close all menus
      this.closeAllMenus();

      // Save the selected standard variant for later use
      this.lastSelectedStandardVariantUrl = variant.url;
      this.standardVariant = variant.label;

      // Persist to localStorage before navigation
      this.saveToLocalStorage();

      // window.location.href = variant.url;

      if (location.hostname === "localhost") {
        this.router.navigate([variant.url]);
      }
      else {
        location.href = `/calculators${variant.url}`;
      }
    }
  }

  /**
   * Handle calculator change from dropdown
   */
  // onCalculationChange(calculator: CalculatorNavItem): void {
  //   if (calculator.disabled || !calculator.url) {
  //     return;
  //   }

  //   if (calculator.external) {
  //     window.location.href = calculator.url;
  //   } else {
  //     this.router.navigate([calculator.url]);
  //   }
  // }

  /**
   * Generate report button click
   */
  @RequireLogin()
  onReportClick(): void {
    this.closeMobileMenu();
    this.sharedService.triggerAction('report');
  }

  /**
   * Calculation steps button click
   */
  onCalculationStep(): void {
    if (this._appSessionService.hasPermission(this.FeatureCode.FDCSCSIS)) {
      this.sharedService.triggerAction('calculationstep');
    }
    else {
      this._appSessionService.showUpgradeAlert();
    }
  }

  /**
   * Navigate to all projects page
   */
  onAllProjectsClick(): void {
    window.location.href = AppConsts.appBaseUrl;
  }

  /**
   * Upgrade plan
   */
  UpgradePlan(): void {
    if (location.hostname === "localhost") {
      this.router.navigate(['/pricing']);
    }
    else {
      location.href = '/calculators/pricing';
    }
  }

  /**
   * Show login modal
   */
  onLogin(): void {
    this.closeMobileMenu();
    this.modalService.open(WebfacingLoginComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      animation: true
    });
  }

  /**
   * Show register modal
   */
  onRegister(): void {
    this.closeMobileMenu();
    this.modalService.open(WebfacingRegisterComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      animation: true
    });
  }

  /**
   * Logout
   */
  logout(): void {
    this.closeMobileMenu();
    this._authService.logout();
  }

  // /**
  //  * Get latest plan name
  //  */
  // getLatestPlanName(planLevel: number): string {
  //   switch (planLevel) {
  //     case 1:
  //       return "ESSENTIAL";
  //     case 2:
  //       return "PREMIUM";
  //     case 3:
  //       return "PROFESSIONAL";
  //     default:
  //       return "";
  //   }
  // }

  /**
   * Get full calculator title with standard variant
   */
  getFullCalculatorTitle(isSort: boolean = false): string {
    if (isSort) {
      const calc = this.getCalculatorFromRoute(this._router.url);
      return calc ? calc.sortName : '';
    }
    if (this.isCableSizingPage && this.standardVariant) {
      return `${this.calculatorName} ${this.standardVariant}`;
    }
    return this.calculatorName;
  }

  isReportButtonDisplay(url: string): boolean {
    const reportUrls = ['/cable-sizing-as', '/cable-sizing-bs', '/cable-sizing-nz', '/cable-sizing-nec', '/cable-pulling', '/arc-flash', '/max-demand', '/protection-coordination', '/cable-sizing-iec'];
    return reportUrls.some(route => url.includes(route));
  }

  redirectToFullAppUrl() {
    let asNzUrl = "https://elek.com/electrical-software/elek-cable-pro-web/";
    let bsUrl = "https://elek.com/electrical-software/elek-cable-pro-web-bs7671/";
    let redirectUrl = "";

    let url = this.router.url;
    if (url.includes('/cable-sizing-as') || url.includes('/cable-sizing-nz')) {
      redirectUrl = asNzUrl;
    } else if (url.includes('/cable-sizing-bs')) {
      redirectUrl = bsUrl;
    } else {
      redirectUrl = asNzUrl;
    }

    window.open(redirectUrl, '_blank', 'noopener,noreferrer');
  }

}
